/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetAllDataChecksParams,
  TLBulkUpdateResponse,
  TLDataCheckBulkUpdateRequest,
  TLDataCheckCodeUpdateRequest,
  TLDataChecksResponse,
  TLDataCheckUpdateRequest,
  TLHTTPValidationError,
  TLSuccessResponse,
  TLValidateCountQueryRequest,
  TLValidateCountQueryResponse,
  UpdateDataCheckCodePathParameters,
  UpdateDataCheckPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get All Data Checks
 */
export const getAllDataChecks = (
  params?: GetAllDataChecksParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLDataChecksResponse>(
    { url: `/v2/data-harmonization/data-checks`, method: 'GET', params, signal },
    options,
  );
};

export const getGetAllDataChecksQueryKey = (params?: GetAllDataChecksParams) => {
  return ['v2', 'data-harmonization', 'data-checks', ...(params ? [params] : [])] as const;
};

export const getGetAllDataChecksQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllDataChecksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllDataChecksQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllDataChecks>>> = ({ signal }) =>
    getAllDataChecks(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllDataChecks>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllDataChecksQueryResult = NonNullable<Awaited<ReturnType<typeof getAllDataChecks>>>;
export type GetAllDataChecksQueryError = ErrorType<TLHTTPValidationError>;

export function useGetAllDataChecks<
  TData = Awaited<ReturnType<typeof getAllDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetAllDataChecksParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDataChecks>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllDataChecks>>,
          TError,
          Awaited<ReturnType<typeof getAllDataChecks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllDataChecks<
  TData = Awaited<ReturnType<typeof getAllDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllDataChecksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDataChecks>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllDataChecks>>,
          TError,
          Awaited<ReturnType<typeof getAllDataChecks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllDataChecks<
  TData = Awaited<ReturnType<typeof getAllDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllDataChecksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Data Checks
 */

export function useGetAllDataChecks<
  TData = Awaited<ReturnType<typeof getAllDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetAllDataChecksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllDataChecksQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Data Checks
 */
export const prefetchGetAllDataChecksQuery = async <
  TData = Awaited<ReturnType<typeof getAllDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetAllDataChecksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllDataChecksQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Data Checks
 */
export const invalidateGetAllDataChecks = async (
  queryClient: QueryClient,
  params?: GetAllDataChecksParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllDataChecksQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Bulk Update Data Checks
 */
export const bulkUpdateDataChecks = (
  tLDataCheckBulkUpdateRequest: BodyType<TLDataCheckBulkUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLBulkUpdateResponse>(
    {
      url: `/v2/data-harmonization/data-checks/bulk`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataCheckBulkUpdateRequest,
      signal,
    },
    options,
  );
};

export const getBulkUpdateDataChecksQueryKey = (
  tLDataCheckBulkUpdateRequest?: BodyType<TLDataCheckBulkUpdateRequest>,
) => {
  return ['PATCH', 'v2', 'data-harmonization', 'data-checks', 'bulk', tLDataCheckBulkUpdateRequest] as const;
};

export const getBulkUpdateDataChecksQueryOptions = <
  TData = Awaited<ReturnType<typeof bulkUpdateDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataCheckBulkUpdateRequest: BodyType<TLDataCheckBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getBulkUpdateDataChecksQueryKey(tLDataCheckBulkUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof bulkUpdateDataChecks>>> = ({ signal }) =>
    bulkUpdateDataChecks(tLDataCheckBulkUpdateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof bulkUpdateDataChecks>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type BulkUpdateDataChecksQueryResult = NonNullable<Awaited<ReturnType<typeof bulkUpdateDataChecks>>>;
export type BulkUpdateDataChecksQueryError = ErrorType<TLHTTPValidationError>;

export function useBulkUpdateDataChecks<
  TData = Awaited<ReturnType<typeof bulkUpdateDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataCheckBulkUpdateRequest: BodyType<TLDataCheckBulkUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDataChecks>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkUpdateDataChecks>>,
          TError,
          Awaited<ReturnType<typeof bulkUpdateDataChecks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkUpdateDataChecks<
  TData = Awaited<ReturnType<typeof bulkUpdateDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataCheckBulkUpdateRequest: BodyType<TLDataCheckBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDataChecks>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof bulkUpdateDataChecks>>,
          TError,
          Awaited<ReturnType<typeof bulkUpdateDataChecks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useBulkUpdateDataChecks<
  TData = Awaited<ReturnType<typeof bulkUpdateDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataCheckBulkUpdateRequest: BodyType<TLDataCheckBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Bulk Update Data Checks
 */

export function useBulkUpdateDataChecks<
  TData = Awaited<ReturnType<typeof bulkUpdateDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLDataCheckBulkUpdateRequest: BodyType<TLDataCheckBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getBulkUpdateDataChecksQueryOptions(tLDataCheckBulkUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Bulk Update Data Checks
 */
export const prefetchBulkUpdateDataChecksQuery = async <
  TData = Awaited<ReturnType<typeof bulkUpdateDataChecks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLDataCheckBulkUpdateRequest: BodyType<TLDataCheckBulkUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof bulkUpdateDataChecks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getBulkUpdateDataChecksQueryOptions(tLDataCheckBulkUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Bulk Update Data Checks
 */
export const invalidateBulkUpdateDataChecks = async (
  queryClient: QueryClient,
  tLDataCheckBulkUpdateRequest: BodyType<TLDataCheckBulkUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getBulkUpdateDataChecksQueryKey(tLDataCheckBulkUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Validate Count Query
 */
export const validateCountQuery = (
  tLValidateCountQueryRequest: BodyType<TLValidateCountQueryRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLValidateCountQueryResponse>(
    {
      url: `/v2/data-harmonization/data-checks/validate-count-query`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLValidateCountQueryRequest,
      signal,
    },
    options,
  );
};

export const getValidateCountQueryQueryKey = (tLValidateCountQueryRequest?: BodyType<TLValidateCountQueryRequest>) => {
  return [
    'POST',
    'v2',
    'data-harmonization',
    'data-checks',
    'validate-count-query',
    tLValidateCountQueryRequest,
  ] as const;
};

export const getValidateCountQueryQueryOptions = <
  TData = Awaited<ReturnType<typeof validateCountQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLValidateCountQueryRequest: BodyType<TLValidateCountQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateCountQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getValidateCountQueryQueryKey(tLValidateCountQueryRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof validateCountQuery>>> = ({ signal }) =>
    validateCountQuery(tLValidateCountQueryRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof validateCountQuery>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ValidateCountQueryQueryResult = NonNullable<Awaited<ReturnType<typeof validateCountQuery>>>;
export type ValidateCountQueryQueryError = ErrorType<TLHTTPValidationError>;

export function useValidateCountQuery<
  TData = Awaited<ReturnType<typeof validateCountQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLValidateCountQueryRequest: BodyType<TLValidateCountQueryRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateCountQuery>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateCountQuery>>,
          TError,
          Awaited<ReturnType<typeof validateCountQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateCountQuery<
  TData = Awaited<ReturnType<typeof validateCountQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLValidateCountQueryRequest: BodyType<TLValidateCountQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateCountQuery>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof validateCountQuery>>,
          TError,
          Awaited<ReturnType<typeof validateCountQuery>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useValidateCountQuery<
  TData = Awaited<ReturnType<typeof validateCountQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLValidateCountQueryRequest: BodyType<TLValidateCountQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateCountQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Validate Count Query
 */

export function useValidateCountQuery<
  TData = Awaited<ReturnType<typeof validateCountQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLValidateCountQueryRequest: BodyType<TLValidateCountQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateCountQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getValidateCountQueryQueryOptions(tLValidateCountQueryRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Validate Count Query
 */
export const prefetchValidateCountQueryQuery = async <
  TData = Awaited<ReturnType<typeof validateCountQuery>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLValidateCountQueryRequest: BodyType<TLValidateCountQueryRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof validateCountQuery>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getValidateCountQueryQueryOptions(tLValidateCountQueryRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Validate Count Query
 */
export const invalidateValidateCountQuery = async (
  queryClient: QueryClient,
  tLValidateCountQueryRequest: BodyType<TLValidateCountQueryRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getValidateCountQueryQueryKey(tLValidateCountQueryRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Data Check
 */
export const updateDataCheck = (
  { dataCheckId }: UpdateDataCheckPathParameters,
  tLDataCheckUpdateRequest: BodyType<TLDataCheckUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/v2/data-harmonization/data-checks/${dataCheckId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataCheckUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateDataCheckQueryKey = (
  { dataCheckId }: UpdateDataCheckPathParameters,
  tLDataCheckUpdateRequest?: BodyType<TLDataCheckUpdateRequest>,
) => {
  return ['PATCH', 'v2', 'data-harmonization', 'data-checks', dataCheckId, tLDataCheckUpdateRequest] as const;
};

export const getUpdateDataCheckQueryOptions = <
  TData = Awaited<ReturnType<typeof updateDataCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dataCheckId }: UpdateDataCheckPathParameters,
  tLDataCheckUpdateRequest: BodyType<TLDataCheckUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateDataCheckQueryKey({ dataCheckId }, tLDataCheckUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateDataCheck>>> = ({ signal }) =>
    updateDataCheck({ dataCheckId }, tLDataCheckUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dataCheckId !== null && dataCheckId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateDataCheck>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateDataCheckQueryResult = NonNullable<Awaited<ReturnType<typeof updateDataCheck>>>;
export type UpdateDataCheckQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateDataCheck<
  TData = Awaited<ReturnType<typeof updateDataCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDataCheckPathParameters,
  tLDataCheckUpdateRequest: BodyType<TLDataCheckUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheck>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDataCheck>>,
          TError,
          Awaited<ReturnType<typeof updateDataCheck>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDataCheck<
  TData = Awaited<ReturnType<typeof updateDataCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDataCheckPathParameters,
  tLDataCheckUpdateRequest: BodyType<TLDataCheckUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheck>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDataCheck>>,
          TError,
          Awaited<ReturnType<typeof updateDataCheck>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDataCheck<
  TData = Awaited<ReturnType<typeof updateDataCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDataCheckPathParameters,
  tLDataCheckUpdateRequest: BodyType<TLDataCheckUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Data Check
 */

export function useUpdateDataCheck<
  TData = Awaited<ReturnType<typeof updateDataCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dataCheckId }: UpdateDataCheckPathParameters,
  tLDataCheckUpdateRequest: BodyType<TLDataCheckUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateDataCheckQueryOptions({ dataCheckId }, tLDataCheckUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Data Check
 */
export const prefetchUpdateDataCheckQuery = async <
  TData = Awaited<ReturnType<typeof updateDataCheck>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dataCheckId }: UpdateDataCheckPathParameters,
  tLDataCheckUpdateRequest: BodyType<TLDataCheckUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheck>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateDataCheckQueryOptions({ dataCheckId }, tLDataCheckUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Data Check
 */
export const invalidateUpdateDataCheck = async (
  queryClient: QueryClient,
  { dataCheckId }: UpdateDataCheckPathParameters,
  tLDataCheckUpdateRequest: BodyType<TLDataCheckUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateDataCheckQueryKey({ dataCheckId }, tLDataCheckUpdateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Data Check Code
 */
export const updateDataCheckCode = (
  { dataCheckId }: UpdateDataCheckCodePathParameters,
  tLDataCheckCodeUpdateRequest: BodyType<TLDataCheckCodeUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/v2/data-harmonization/data-checks/${dataCheckId}/code`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLDataCheckCodeUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateDataCheckCodeQueryKey = (
  { dataCheckId }: UpdateDataCheckCodePathParameters,
  tLDataCheckCodeUpdateRequest?: BodyType<TLDataCheckCodeUpdateRequest>,
) => {
  return ['PUT', 'v2', 'data-harmonization', 'data-checks', dataCheckId, 'code', tLDataCheckCodeUpdateRequest] as const;
};

export const getUpdateDataCheckCodeQueryOptions = <
  TData = Awaited<ReturnType<typeof updateDataCheckCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dataCheckId }: UpdateDataCheckCodePathParameters,
  tLDataCheckCodeUpdateRequest: BodyType<TLDataCheckCodeUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheckCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateDataCheckCodeQueryKey({ dataCheckId }, tLDataCheckCodeUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateDataCheckCode>>> = ({ signal }) =>
    updateDataCheckCode({ dataCheckId }, tLDataCheckCodeUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: dataCheckId !== null && dataCheckId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateDataCheckCode>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateDataCheckCodeQueryResult = NonNullable<Awaited<ReturnType<typeof updateDataCheckCode>>>;
export type UpdateDataCheckCodeQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateDataCheckCode<
  TData = Awaited<ReturnType<typeof updateDataCheckCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDataCheckCodePathParameters,
  tLDataCheckCodeUpdateRequest: BodyType<TLDataCheckCodeUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheckCode>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDataCheckCode>>,
          TError,
          Awaited<ReturnType<typeof updateDataCheckCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDataCheckCode<
  TData = Awaited<ReturnType<typeof updateDataCheckCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDataCheckCodePathParameters,
  tLDataCheckCodeUpdateRequest: BodyType<TLDataCheckCodeUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheckCode>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateDataCheckCode>>,
          TError,
          Awaited<ReturnType<typeof updateDataCheckCode>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateDataCheckCode<
  TData = Awaited<ReturnType<typeof updateDataCheckCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateDataCheckCodePathParameters,
  tLDataCheckCodeUpdateRequest: BodyType<TLDataCheckCodeUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheckCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Data Check Code
 */

export function useUpdateDataCheckCode<
  TData = Awaited<ReturnType<typeof updateDataCheckCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { dataCheckId }: UpdateDataCheckCodePathParameters,
  tLDataCheckCodeUpdateRequest: BodyType<TLDataCheckCodeUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheckCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateDataCheckCodeQueryOptions({ dataCheckId }, tLDataCheckCodeUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Data Check Code
 */
export const prefetchUpdateDataCheckCodeQuery = async <
  TData = Awaited<ReturnType<typeof updateDataCheckCode>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { dataCheckId }: UpdateDataCheckCodePathParameters,
  tLDataCheckCodeUpdateRequest: BodyType<TLDataCheckCodeUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateDataCheckCode>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateDataCheckCodeQueryOptions({ dataCheckId }, tLDataCheckCodeUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Data Check Code
 */
export const invalidateUpdateDataCheckCode = async (
  queryClient: QueryClient,
  { dataCheckId }: UpdateDataCheckCodePathParameters,
  tLDataCheckCodeUpdateRequest: BodyType<TLDataCheckCodeUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateDataCheckCodeQueryKey({ dataCheckId }, tLDataCheckCodeUpdateRequest) },
    options,
  );

  return queryClient;
};
